import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { EntityStatus, FeaturedMember, Group, User, YammerState } from '../../state/types';
import { createFindById, createFindByIdThrows } from '../findById';
import { getFulfilledUsers } from '../users/selectors';

export const findById = createFindById('groupMemberPanels');
export const findByIdThrows = createFindByIdThrows('groupMemberPanels');

type GetStatusForId = (state: YammerState, id: Group['id']) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, id) => getStatus(state.groupMemberPanels, id);

type IsLoaded = (state: YammerState, id: Group['id']) => boolean;
export const isLoaded: IsLoaded = (state, id) => getStatusForId(state, id) === 'fulfilled';

type GetHasMoreMembersOnServer = (state: YammerState, id: Group['id']) => boolean;
export const hasMoreMembersOnServer: GetHasMoreMembersOnServer = (state, id) => {
  const groupMembers = findById(state, id);

  return groupMembers ? groupMembers.hasMoreMembers : true;
};

export const getLastCachedGroupMemberEdge = createCachedSelector(findById, (groupMembers) =>
  groupMembers && groupMembers.members ? groupMembers.members.slice(-1)[0] : undefined
)((_state, id) => id);

export const getGroupMembers = createCachedSelector(findByIdThrows, getFulfilledUsers, (groupMembers, users) =>
  groupMembers.members.reduce<FeaturedMember[]>((members, edge) => {
    const user = users[edge.nodeId];
    if (user) {
      members.push({
        ...user,
        isAdmin: edge.isAdmin,
      });
    }

    return members;
  }, [])
)((_state, id) => id);

type GetGroupAdminMemberIds = (state: YammerState, groupId: Group['id']) => User['id'][];
export const getGroupAdminMemberIds: GetGroupAdminMemberIds = createCachedSelector(
  findById,
  getFulfilledUsers,
  (groupMembers, users) => {
    if (!groupMembers) {
      return [];
    }

    return groupMembers.members.reduce<User['id'][]>((admins, edge) => {
      const user = users[edge.nodeId];
      if (user && edge.isAdmin) {
        admins.push(user.id);
      }

      return admins;
    }, []);
  }
)((_state, groupId) => groupId);
